import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, ArrowLeft, CreditCard, Gift, Sparkles, Users, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API } from '@/App';

function PricingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [foundingStatus, setFoundingStatus] = useState(null);
  const [showInviteCode, setShowInviteCode] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);

  useEffect(() => {
    fetchFoundingStatus();
  }, []);

  const fetchFoundingStatus = async () => {
    try {
      const response = await axios.get(`${API}/promo/founding-member-status`);
      setFoundingStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch founding status:', error);
    }
  };

  const handleCheckout = async (packageId) => {
    setLoading(packageId);

    try {
      const originUrl = window.location.origin;

      const response = await axios.post(
        `${API}/payments/checkout`,
        {
          package_id: packageId,
          origin_url: originUrl
        }
      );

      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setLoading(null);
    }
  };

  const handleFoundingMemberSignup = () => {
    navigate('/register-free?type=founding');
  };

  const handleInviteCodeSubmit = async () => {
    if (!inviteCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }

    setValidatingCode(true);
    try {
      const response = await axios.post(`${API}/promo/validate-invite-code`, {
        code: inviteCode.trim()
      });

      if (response.data.valid) {
        toast.success('Valid code! Redirecting to signup...');
        navigate(`/register-free?type=invite&code=${inviteCode.trim().toUpperCase()}`);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to validate code');
    } finally {
      setValidatingCode(false);
    }
  };

  const spotsRemaining = foundingStatus?.spots_remaining ?? 0;
  const spotsTaken = foundingStatus?.spots_taken ?? 0;
  const totalSpots = foundingStatus?.total_spots ?? 50;
  const progressPercent = (spotsTaken / totalSpots) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-secondary-50 py-12 px-6">
      <div className="container mx-auto max-w-6xl">
        <Button
          data-testid="back-button"
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-8 text-secondary-900"
        >
          <ArrowLeft className="mr-2 w-5 h-5" /> Back
        </Button>

        {/* Founding Member Banner - Only show if spots available */}
        {foundingStatus?.is_available && (
          <div className="mb-12 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 rounded-3xl p-1">
            <div className="bg-white rounded-3xl p-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Sparkles className="w-6 h-6 text-amber-500" />
                <span className="text-sm font-bold text-amber-600 uppercase tracking-wide">
                  Limited Time Offer
                </span>
                <Sparkles className="w-6 h-6 text-amber-500" />
              </div>
              
              <h2 className="text-3xl font-serif font-bold text-center text-secondary-900 mb-2">
                Become a Founding Member - FREE!
              </h2>
              <p className="text-center text-secondary-600 mb-6">
                Be one of the first 50 women to join Radiant 40 completely free
              </p>

              {/* Progress Bar */}
              <div className="max-w-md mx-auto mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-secondary-600">{spotsTaken} joined</span>
                  <span className="font-bold text-rose-600">{spotsRemaining} spots left!</span>
                </div>
                <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-400 to-rose-500 transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  onClick={handleFoundingMemberSignup}
                  className="bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  <Gift className="w-5 h-5 mr-2" />
                  Claim Your Free Spot
                </Button>
              </div>

              <p className="text-center text-sm text-secondary-500 mt-4">
                Full 12-week program • All features included • No credit card required
              </p>
            </div>
          </div>
        )}

        {/* Invite Code Section */}
        <div className="mb-12 bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-full">
                <Ticket className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-secondary-900">Have an invite code?</h3>
                <p className="text-sm text-secondary-600">Enter it here for free access</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowInviteCode(!showInviteCode)}
              className="border-purple-300 text-purple-600 hover:bg-purple-50"
            >
              {showInviteCode ? 'Hide' : 'Enter Code'}
            </Button>
          </div>

          {showInviteCode && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex gap-3 max-w-md">
                <Input
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder="ENTER CODE"
                  className="font-mono text-lg tracking-wider uppercase"
                />
                <Button
                  onClick={handleInviteCodeSubmit}
                  disabled={validatingCode || !inviteCode.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6"
                >
                  {validatingCode ? 'Checking...' : 'Apply'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Regular Pricing Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-4">
            {foundingStatus?.is_available ? 'Or Choose a Paid Plan' : 'Choose Your Plan'}
          </h1>
          <p className="text-xl text-secondary-700">
            Invest in yourself - you're worth it
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Monthly Plan */}
          <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-secondary-100 hover:border-primary/50 transition-all duration-300">
            <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-2">Monthly</h2>
            <p className="text-secondary-600 mb-6">Perfect for trying it out</p>
            <div className="mb-6">
              <span className="text-5xl font-bold text-secondary-900">$9.99</span>
              <span className="text-secondary-600">/4 weeks</span>
            </div>
            <ul className="space-y-3 mb-8">
              {['Access to 4 weeks of content', 'Daily tasks and guides', 'Progress tracking', 'Community access'].map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-secondary-700">
                  <Check className="w-5 h-5 text-primary" /> {feature}
                </li>
              ))}
            </ul>
            <Button
              data-testid="checkout-monthly-button"
              onClick={() => handleCheckout('monthly')}
              disabled={loading === 'monthly'}
              variant="outline"
              className="w-full py-6 text-lg rounded-full border-2 border-secondary-300 hover:border-primary hover:bg-primary/5"
            >
              {loading === 'monthly' ? 'Processing...' : 'Start Monthly'}
            </Button>
          </div>

          {/* Full Course - Highlighted */}
          <div className="bg-gradient-to-br from-secondary-800 to-secondary-900 rounded-3xl shadow-xl p-8 text-white relative overflow-hidden">
            <div className="absolute top-4 right-4 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
              BEST VALUE
            </div>
            <h2 className="text-2xl font-serif font-bold mb-2">Full Course</h2>
            <p className="text-secondary-300 mb-6">The complete transformation</p>
            <div className="mb-6">
              <span className="text-5xl font-bold">$24.99</span>
              <span className="text-secondary-300"> one-time</span>
            </div>
            <ul className="space-y-3 mb-8">
              {[
                'All 12 weeks unlocked',
                '84 daily tasks and guides',
                'Flower garden visualization',
                'Completion certificate',
                'Community access',
                'Lifetime access'
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-primary" /> {feature}
                </li>
              ))}
            </ul>
            <Button
              data-testid="checkout-full-button"
              onClick={() => handleCheckout('full_course')}
              disabled={loading === 'full_course'}
              className="w-full bg-white hover:bg-secondary-50 text-secondary py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
            >
              {loading === 'full_course' ? (
                'Processing...'
              ) : (
                <>
                  <CreditCard className="mr-2 w-5 h-5" /> Get Full Course
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Already have an account section */}
        <div className="mt-12 text-center">
          <p className="text-secondary-700 mb-4">Already have an account?</p>
          <Button
            data-testid="login-button"
            onClick={() => navigate('/auth')}
            variant="outline"
            className="px-8 py-3 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300"
          >
            Sign In
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PricingPage;
